/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LegalHero.module.scss';

type OwnershipRow = {
  name: string;
  code: string;
  meta: string;
  depth: 0 | 1 | 2 | 3;
  mid?: boolean;
  flag?: string;
};

const QUERY = 'Halden Marine Services Ltd';
const TYPE_MS = 46;
const TYPE_START_MS = 380;
const ROW_GAP_MS = 250;
const FLAG_GAP_MS = 560;

const ROWS: OwnershipRow[] = [
  { name: 'Halden Marine Services Ltd', code: 'gb', meta: '11482096 · active', depth: 0 },
  { name: 'Halden Group Holdings BV', code: 'nl', meta: 'parent · 74.0%', depth: 1 },
  { name: 'Brant Maritime Holdings SA', code: 'lu', meta: 'ultimate parent', depth: 2 },
  {
    name: 'Kestrel Offshore GmbH',
    code: 'de',
    meta: 'sister · 100%',
    depth: 3,
    mid: true,
    flag: 'Adverse party — matter 2024-1187, acting against',
  },
  { name: 'Brant Chartering Pte Ltd', code: 'sg', meta: 'sister · 100%', depth: 3, mid: true },
  { name: 'Adlerhaven Bunkering AS', code: 'no', meta: 'sister · 51%', depth: 3 },
];

const rowDelay = (i: number) => TYPE_START_MS + QUERY.length * TYPE_MS + 200 + i * (i === 3 ? FLAG_GAP_MS : ROW_GAP_MS);

const TOTAL_MS = rowDelay(ROWS.length - 1) + 500;

export const LegalHero = () => {
  const { t } = useInt();
  const cardRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(cardRef, { threshold: 0.2 });
  const [playKey, setPlayKey] = useState(0);
  const [typedLen, setTypedLen] = useState(0);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    setTypedLen(0);
    setConflict(false);

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= QUERY.length; i += 1) {
      timers.push(setTimeout(() => setTypedLen(i), TYPE_START_MS + i * TYPE_MS));
    }
    timers.push(setTimeout(() => setConflict(true), TOTAL_MS));

    return () => timers.forEach(clearTimeout);
  }, [revealed, playKey]);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Company evidence a file can stand on.')}
            description={
              <>
                {t('Primary records from')} <b>{t('400+ government registries')}</b> {t('across')}{' '}
                <b>{t('200+ countries')}</b>.{' '}
                {t(
                  'Every field carries the registry it came from and the date it was pulled. Good enough for an intake file. Good enough for an exhibit.',
                )}
              </>
            }
          />
          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/book-a-demo">
              {t('Book a demo')}
            </Button>
            <Button type="dark-outline" size="xxl" href="/free-trial">
              {t('Run a group structure free')}
            </Button>
          </div>
        </div>

        <div className={styles.cardWrap} ref={cardRef}>
          <div className={cn(styles.card, revealed && styles.scanning)} key={playKey}>
            <div className={styles.scanline} />

            <div className={styles.cardHead}>
              <span className={styles.cardLabel}>Intake check — proposed client</span>
              <span className={cn(styles.status, conflict && styles.statusHit)}>
                <i />
                {conflict ? 'Conflict found' : 'Checking'}
              </span>
            </div>

            <div className={styles.query}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>
              <span>{QUERY.slice(0, typedLen)}</span>
              <span className={styles.caret} />
            </div>

            <div className={styles.body}>
              {ROWS.map((row, i) => (
                <div
                  className={cn(
                    styles.row,
                    styles[`depth${row.depth}`],
                    row.mid && styles.mid,
                    row.flag && styles.flagged,
                  )}
                  style={{ '--d': `${rowDelay(i)}ms` } as CSSProperties}
                  key={row.name}
                >
                  <div className={styles.rowMain}>
                    <span className={styles.rowId}>
                      <Flag code={row.code} className={styles.rowFlag} rounded />
                      <span className={styles.rowName}>{row.name}</span>
                    </span>
                    <span className={styles.rowMeta}>{row.meta}</span>
                  </div>
                  {row.flag && (
                    <div className={styles.rowNote}>
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                        <path d="M8 2.6l6 10.8H2z" strokeLinejoin="round" />
                        <path d="M8 6.6v3.2M8 11.6v.1" strokeLinecap="round" />
                      </svg>
                      {row.flag}
                    </div>
                  )}
                </div>
              ))}

              <div className={styles.more} style={{ '--d': `${rowDelay(ROWS.length)}ms` } as CSSProperties}>
                + 25 further entities across 7 jurisdictions
              </div>
            </div>

            <div className={styles.cardFoot}>
              <span>One name cleared. Thirty-one entities behind it.</span>
              <button type="button" className={styles.replay} onClick={() => setPlayKey((key) => key + 1)}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" strokeLinecap="round" />
                  <path d="M13.5 2.6v3.6h-3.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Replay
              </button>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
